import { cn } from "cn";
import * as React from "react";
import {
  createKelvMarkGuard,
  type KelvMarkGuard,
  playKelvMarkInBrowser,
} from "../lib/kelv-mark-motion";

export interface LogoProps extends React.ComponentProps<"svg"> {
  /** `mark` — одни полосы, `lockup` — полосы и слово рядом. */
  variant?: "mark" | "lockup";
  /**
   * По наведению играет «Провал уровня» (1,2 с). Повтор до конца игры
   * блокируется; при «уменьшить движение» знак не двигается. Слово в `lockup`
   * неподвижно.
   */
  animated?: boolean;
}

/** Бруски сдвигаются вокруг собственного центра, а не начала координат svg. */
const RECT_CLASS = "origin-center [transform-box:fill-box]";

/** Нижний, средний, верхний бруски и уровень: порядок, в котором их ждёт анимация. */
const BAR_COUNT = 4;

/**
 * Знак продукта из дизайн-системы: четыре полосы, сужающиеся кверху, и слово.
 *
 * Дизайн-система отдаёт знак двумя файлами — под светлый фон и под тёмный, —
 * различающимися только цветом полос. Здесь вместо двух файлов один компонент:
 * полосы берут `currentColor` и потому следуют теме сами, а верхняя полоса
 * остаётся фирменным акцентом `--brand`, одинаковым в обеих темах.
 *
 * Пропорции знака — из исходного SVG и менять их на глаз нельзя: полосы
 * подогнаны так, что при уменьшении до размера значка не сливаются.
 */
export function Logo({
  variant = "mark",
  animated = false,
  className,
  onMouseEnter,
  ...props
}: LogoProps) {
  const lockup = variant === "lockup";
  const bars = React.useRef<Array<SVGRectElement | null>>(
    Array.from({ length: BAR_COUNT }, () => null),
  );
  const guard = React.useRef<KelvMarkGuard | null>(null);

  // Бруски, которые знак когда-либо держал: к очистке эффекта React уже
  // обнулил `bars`, а отменять анимации нужно именно у этих элементов.
  const seen = React.useRef(new Set<SVGRectElement>());

  // Блокировка повтора держится по времени, таймера нет; при размонтировании
  // её снимаем и отменяем идущую игру, чтобы анимация не пережила знак.
  React.useEffect(() => {
    const elements = seen.current;
    return () => {
      guard.current?.reset();
      for (const element of elements) {
        for (const animation of element.getAnimations()) {
          animation.cancel();
        }
      }
      elements.clear();
    };
  }, []);

  const handleMouseEnter = (event: React.MouseEvent<SVGSVGElement>) => {
    onMouseEnter?.(event);
    if (!animated) {
      return;
    }
    guard.current ??= createKelvMarkGuard(() => performance.now());
    const rects = bars.current.filter((rect): rect is SVGRectElement => rect !== null);
    if (rects.length !== BAR_COUNT) {
      return;
    }
    guard.current.run(() => playKelvMarkInBrowser(rects));
  };

  const setBar = (index: number) => (element: SVGRectElement | null) => {
    bars.current[index] = element;
    if (element !== null) {
      seen.current.add(element);
    }
  };

  return (
    <svg
      data-slot="logo"
      data-animated={animated ? "true" : undefined}
      role="img"
      aria-label="kelv"
      viewBox={lockup ? "0 0 224 64" : "0 0 72 64"}
      className={cn("h-6 w-auto overflow-visible", className)}
      onMouseEnter={handleMouseEnter}
      {...props}
    >
      <rect
        ref={setBar(3)}
        x="0"
        y="0"
        width="72"
        height="12"
        rx="2.5"
        className={cn("fill-brand", RECT_CLASS)}
      />
      <rect
        ref={setBar(2)}
        x="20"
        y="21"
        width="32"
        height="6"
        rx="2.5"
        fill="currentColor"
        className={RECT_CLASS}
      />
      <rect
        ref={setBar(1)}
        x="10"
        y="33"
        width="52"
        height="10"
        rx="2.5"
        fill="currentColor"
        className={RECT_CLASS}
      />
      <rect
        ref={setBar(0)}
        x="0"
        y="50"
        width="72"
        height="14"
        rx="2.5"
        fill="currentColor"
        className={RECT_CLASS}
      />
      {lockup ? (
        <text
          x="92"
          y="64"
          className="font-heading"
          fontSize="58"
          fontWeight="600"
          letterSpacing="-0.3"
          fill="currentColor"
        >
          kelv
        </text>
      ) : null}
    </svg>
  );
}
